/**
 * Redux middleware that overrides specific error codes based on the current onboarding step.
 * 
 * This middleware intercepts rejected actions with error codes and replaces them with
 * step-specific error codes when a matching override is found. The override is determined
 * by combining the error code with the current onboarding step name.
 * 
 * @example
 * ```typescript
 * // If current step is "contacts_otp_verify_email" and error code is "ERRFLOWOB024"
 * // The error code will be overridden to "ERRFLOWOB024stepVerifyEmailOtp"
 * ```
 * 
 * @param store - The Redux store instance
 * @returns A middleware function that processes actions
 * 
 * @remarks
 * - Only processes actions that are rejected with value and contain an error code
 * - Uses Immer's produce function to create immutable updates to the action payload
 * - Requires the onboarding state to contain step information for proper override matching
 */

import type { Middleware } from "@reduxjs/toolkit";
import { isRejectedWithValue } from "@reduxjs/toolkit";
import { stepNames } from "../constants";
import { produce } from "immer";

type StepName = typeof stepNames[keyof typeof stepNames];
type ErrorKey = `${string}:${StepName}`;

const errorOverrides: Record<ErrorKey, string> = {
  "ERRFLOWOB024:contacts_otp_verify_email": "ERRFLOWOB024stepVerifyEmailOtp",
  "ERRFLOWOB024:parent_data": "ERRFLOWOB024stepVerifyParentData",
};

interface RejectedActionWithPayload {
  type: string;
  payload: {
    data: {
      errorCode: string;
    };
  };
  meta?: Record<string, unknown>;
}

export const errorOverridesMiddleware: Middleware = store => next => action => {
  const errorCode = action.payload?.data?.errorCode;
  if (!isRejectedWithValue(action) || !errorCode) {
    return next(action);
  }
  const state = store.getState();
  const currentStep: StepName = state?.onboarding?.data?.step?.stepInfo?.name;
  const key = `${errorCode}:${currentStep}`;
  if (key in errorOverrides) {
    return next(
      produce(action as RejectedActionWithPayload, draft => {
        draft.payload.data.errorCode = errorOverrides[key as ErrorKey];
      }),
    );
  }
  return next(action);
};
